import { useEffect, useState, useCallback } from "react";
import { api, formatDate, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/shared/Common";
import { DateRangePicker } from "@/components/shared/DateRangePicker";
import { Pagination } from "@/components/shared/Pagination";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Slider } from "@/components/ui/slider";
import { Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Allocation() {
  const [rules, setRules] = useState([]);
  const [history, setHistory] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [running, setRunning] = useState(false);
  const [dateRange, setDateRange] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [newRule, setNewRule] = useState({
    name: "Custom Rule",
    priority_weight: 0.25,
    skill_weight: 0.30,
    capacity_weight: 0.20,
    performance_weight: 0.15,
    quality_weight: 0.10,
    active: true,
    team_filter: null,
  });

  const loadRules = useCallback(async () => {
    const r = await api.get("/allocation/rules");
    setRules(r.data);
  }, []);

  const loadHistory = useCallback(async () => {
    const q = new URLSearchParams();
    if (dateRange?.from) q.set("date_from", dateRange.from);
    if (dateRange?.to) q.set("date_to", dateRange.to);
    q.set("limit", String(pageSize));
    q.set("skip", String((page - 1) * pageSize));
    const h = await api.get(`/allocation/history?${q.toString()}`);
    setHistory(h.data.items);
    setHistoryTotal(h.data.total || h.data.items.length);
  }, [dateRange, page, pageSize]);

  useEffect(() => { loadRules(); }, [loadRules]);
  useEffect(() => { loadHistory(); }, [loadHistory]);
  useEffect(() => { setPage(1); }, [dateRange, pageSize]);

  const runAuto = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/allocation/auto", { limit: 50 });
      toast.success(`Allocated ${data.allocated} case${data.allocated === 1 ? "" : "s"}`);
      loadHistory();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRunning(false); }
  };

  const activate = async (id) => {
    await api.patch(`/allocation/rules/${id}/activate`);
    toast.success("Rule activated");
    loadRules();
  };

  const totalWeight = ["priority_weight", "skill_weight", "capacity_weight", "performance_weight", "quality_weight"]
    .reduce((s, k) => s + (newRule[k] || 0), 0);

  const createRule = async () => {
    try {
      await api.post("/allocation/rules", newRule);
      toast.success("Rule created & activated");
      loadRules();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const WeightSlider = ({ label, k }) => (
    <div>
      <div className="flex items-center justify-between">
        <Label className="eyebrow text-muted-foreground">{label}</Label>
        <span className="mono text-xs">{(newRule[k] * 100).toFixed(0)}%</span>
      </div>
      <Slider
        data-testid={`weight-${k}`}
        value={[newRule[k] * 100]}
        onValueChange={(v) => setNewRule(r => ({ ...r, [k]: v[0] / 100 }))}
        min={0} max={100} step={5} className="mt-2"
      />
    </div>
  );

  return (
    <div>
      <PageHeader title="Allocation Engine" subtitle="Configure automated work distribution and monitor allocation history">
        <Button data-testid="run-auto-alloc-btn" onClick={runAuto} disabled={running} className="h-9">
          <Sparkle size={14} className="mr-1" /> {running ? "Running…" : "Run auto-allocation"}
        </Button>
      </PageHeader>

      <Tabs defaultValue="rules" className="w-full">
        <TabsList className="h-auto">
          <TabsTrigger value="rules" data-testid="tab-rules">Rules</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">Allocation history</TabsTrigger>
          <TabsTrigger value="create" data-testid="tab-create-rule">Create rule</TabsTrigger>
        </TabsList>

        <TabsContent value="rules">
          <div className="border rounded-md bg-card divide-y">
            {rules.map(r => (
              <div key={r.id} className="p-5 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <div className="font-semibold">{r.name}</div>
                    {r.active && <span className="px-2 py-0.5 text-[10px] bg-emerald-100 text-emerald-800 rounded uppercase tracking-widest">Active</span>}
                  </div>
                  <div className="text-xs text-muted-foreground mono mt-1">
                    Skill {(r.skill_weight*100).toFixed(0)}% · Priority {(r.priority_weight*100).toFixed(0)}% · Capacity {(r.capacity_weight*100).toFixed(0)}% · Performance {(r.performance_weight*100).toFixed(0)}% · Quality {(r.quality_weight*100).toFixed(0)}%
                  </div>
                </div>
                {!r.active && <Button size="sm" variant="outline" onClick={() => activate(r.id)}>Activate</Button>}
              </div>
            ))}
            {rules.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">No rules yet</div>}
          </div>
        </TabsContent>

        <TabsContent value="history">
          <div className="border rounded-md bg-card">
            <div className="p-4 border-b flex justify-end">
              <DateRangePicker testId="history-date-range" value={dateRange} onChange={setDateRange} />
            </div>
            <table className="w-full text-sm">
              <thead className="text-left text-[11px] uppercase tracking-widest text-muted-foreground border-b">
                <tr>
                  <th className="px-4 py-3">Case</th>
                  <th className="px-4 py-3">Assignee</th>
                  <th className="px-4 py-3">Score</th>
                  <th className="px-4 py-3">Method</th>
                  <th className="px-4 py-3">Rule</th>
                  <th className="px-4 py-3">At</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {history.map(h => (
                  <tr key={h.id} className="hover:bg-secondary/40">
                    <td className="px-4 py-3 mono text-xs">{h.case_id?.slice(0, 8)}…</td>
                    <td className="px-4 py-3">{h.assignee_name}</td>
                    <td className="px-4 py-3 mono">{h.score?.toFixed?.(3) ?? "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 text-[10px] rounded uppercase tracking-widest ${h.manual_override ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-700"}`}>
                        {h.manual_override ? "Manual" : "Auto"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{h.rule_name || "—"}</td>
                    <td className="px-4 py-3 text-xs mono text-muted-foreground">{formatDate(h.assigned_at)}</td>
                  </tr>
                ))}
                {history.length === 0 && (
                  <tr><td colSpan={6} className="p-6 text-center text-sm text-muted-foreground">No allocations yet</td></tr>
                )}
              </tbody>
            </table>
            <Pagination testId="history-pagination" page={page} pageSize={pageSize} total={historyTotal} onPageChange={setPage} onPageSizeChange={setPageSize} />
          </div>
        </TabsContent>

        <TabsContent value="create">
          <div className="border rounded-md bg-card p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <Label className="eyebrow text-muted-foreground">Rule name</Label>
              <Input value={newRule.name} onChange={e => setNewRule(r => ({...r, name: e.target.value}))} className="mt-1.5" />
              <div className="mt-4 text-xs text-muted-foreground">
                Total weight: <span className={`mono ${Math.abs(totalWeight - 1) < 0.01 ? "text-emerald-500" : "text-amber-500"}`}>{(totalWeight * 100).toFixed(0)}%</span> (should equal 100%)
              </div>
            </div>
            <div className="space-y-4">
              <WeightSlider label="Skill match" k="skill_weight" />
              <WeightSlider label="Priority alignment" k="priority_weight" />
              <WeightSlider label="Capacity available" k="capacity_weight" />
              <WeightSlider label="Performance score" k="performance_weight" />
              <WeightSlider label="Quality score" k="quality_weight" />
            </div>
            <div className="lg:col-span-2">
              <Button data-testid="create-rule-btn" onClick={createRule}>Create & activate rule</Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
